import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PhotoUpload = ({ photos, setPhotos }) => {
  const fileInputRef = useRef(null);
  const MAX_PHOTOS = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    const validFiles = [];

    for (const file of files) {
      if (photos?.length + validFiles?.length >= MAX_PHOTOS) {
        alert(`Maximum ${MAX_PHOTOS} photos allowed`);
        break;
      }

      if (file?.size > MAX_FILE_SIZE) {
        alert(`${file?.name} is too large. Maximum size is 5MB`);
        continue;
      }

      if (!file?.type?.startsWith('image/')) {
        alert(`${file?.name} is not an image file`);
        continue;
      }

      validFiles?.push(file);
    }

    if (validFiles?.length > 0) {
      setPhotos(prev => [...prev, ...validFiles]);
    }

    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index) => {
    setPhotos(prev => prev?.filter((_, i) => i !== index));
  };

  const getPhotoPreview = (file) => {
    return URL.createObjectURL(file);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
        <Icon name="Camera" size={20} className="mr-2" />
        Photo Upload (Optional)
      </h2>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Upload up to {MAX_PHOTOS} photos (5MB each)
          </p>
          <span className="text-sm font-medium text-foreground">
            {photos?.length}/{MAX_PHOTOS}
          </span>
        </div>

        {photos?.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {photos?.map((photo, index) => (
              <div key={index} className="relative group">
                <img
                  src={getPhotoPreview(photo)}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-border"
                />
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(index)}
                  className="absolute top-2 right-2 bg-error text-error-foreground rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                >
                  <Icon name="X" size={16} />
                </button>
                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {(photo?.size / 1024 / 1024)?.toFixed(1)}MB
                </div>
              </div>
            ))}
          </div>
        )}

        {photos?.length < MAX_PHOTOS && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              size="lg"
              iconName="Upload"
              iconPosition="left"
              onClick={() => fileInputRef?.current?.click()}
              className="w-full"
            >
              {photos?.length === 0 ? 'Add Photos' : 'Add More Photos'}
            </Button>
          </div>
        )}

        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Photos help our technicians diagnose issues faster. Include images of the problem area, dashboard warnings, or any visible damage.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUpload;